import { readAnalyticsConfig } from './config';
import type { AnalyticsEventName, AnalyticsEventProperties, AnalyticsProvider } from './contracts';
import { loadAnalyticsProvider } from './loader';
import { createGoogleProvider, createYandexProvider } from './providers';
import { captureFirstTouchUtm } from './utm';

const config = readAnalyticsConfig();

declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
  }
}

function safePath(path: string): string {
  try {
    const url = new URL(path, window.location.origin);
    return url.pathname;
  } catch {
    return '/';
  }
}

export class AnalyticsClient {
  constructor(private readonly provider: AnalyticsProvider | null) {}

  pageView(path: string): void {
    this.provider?.pageView(safePath(path));
  }

  event<Name extends AnalyticsEventName>(
    name: Name,
    properties: AnalyticsEventProperties[Name]
  ): void {
    this.provider?.event(name, properties);
  }
}

function createBrowserProvider(): AnalyticsProvider | null {
  if (!config.enabled || !config.market || !config.measurementId) return null;
  if (config.market === 'ru') {
    const counterId = Number(config.measurementId);
    return Number.isSafeInteger(counterId)
      ? createYandexProvider(counterId, (...args) => window.ym?.(...args))
      : null;
  }
  return createGoogleProvider((...args) => window.gtag?.(...args));
}

loadAnalyticsProvider(config);
try {
  captureFirstTouchUtm(localStorage, window.location.search);
} catch {
  // Attribution storage is optional and must not disable configured analytics.
}

export const analytics = new AnalyticsClient(createBrowserProvider());
